import type { DateTime } from "luxon";
import { useMemo } from "react";
import type { EmploymentType } from "../../../utils/models";
import { PAGE_SIZE } from "../constants";
import type { ScheduleReportShift, SortField, SortOrder } from "../types";
import { hasTimeOverlap, sortEntries } from "../utils";

export const useScheduleData = (
	shifts: ScheduleReportShift[],
	sortField: SortField,
	sortOrder: SortOrder,
	currentPage: number,
	selectedContracts: EmploymentType[],
	chargeFilter: boolean | null,
	startHourFilter: DateTime | null,
	endHourFilter: DateTime | null,
) => {
	const filteredShifts = useMemo(() => {
		let result = shifts;
		if (selectedContracts.length > 0) {
			result = result.filter((shift) => selectedContracts.includes(shift.employmentType));
		}
		if (chargeFilter !== null) {
			result = result.filter((shift) => shift.charge === chargeFilter);
		}
		if (startHourFilter && endHourFilter) {
			const filterStartMinutes = startHourFilter.hour * 60 + startHourFilter.minute;
			const filterEndMinutes = endHourFilter.hour * 60 + endHourFilter.minute;
			result = result.filter((shift) => hasTimeOverlap(shift.startTime, shift.endTime, filterStartMinutes, filterEndMinutes));
		}
		return result;
	}, [shifts, selectedContracts, chargeFilter, startHourFilter, endHourFilter]);

	const totalFilteredCount = useMemo(() => {
		return filteredShifts.length;
	}, [filteredShifts]);

	const filteredAndSortedEntries = useMemo<ScheduleReportShift[]>(() => {
		return sortEntries(filteredShifts, sortField, sortOrder);
	}, [filteredShifts, sortField, sortOrder]);

	const totalPages = useMemo(() => Math.ceil(totalFilteredCount / PAGE_SIZE), [totalFilteredCount]);

	const paginatedEntries = useMemo(() => {
		const startIndex = (currentPage - 1) * PAGE_SIZE;
		const endIndex = startIndex + PAGE_SIZE;
		return filteredAndSortedEntries.slice(startIndex, endIndex);
	}, [filteredAndSortedEntries, currentPage]);

	return {
		totalFilteredCount,
		filteredAndSortedEntries,
		totalPages,
		paginatedEntries,
	};
};
